import { apiEnums, useApiClient } from '@nodal/api';
import { useCallback, useContext, useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';

import { queryKeys } from 'consts/query';

import { ScreeningContext } from './ScreeningContextProvider';

import type {
  FormSubmitHandler,
  ScreeningProviderProps,
} from './Screening.interface';
import type { ApiModel } from '@nodal/api';

interface ScreenWithCompleteLater extends ApiModel.Screen {
  is_complete_later?: boolean;
}

const getNextStepIndex = (
  steps: ScreeningProviderProps['steps'],
  userRole?: string,
) =>
  steps.findIndex((s) => {
    // Check if the step is Identity Check and has is_complete_later=true
    if (
      s.uid === 'donor-identity' &&
      (s as ScreenWithCompleteLater).is_complete_later === true
    ) {
      return false;
    }

    if (
      s.type === 'insurance-review' &&
      s.status === apiEnums.ScreenStatusEnum.Proc &&
      userRole === 'odo'
    ) {
      return false;
    }

    return (
      s.status !== apiEnums.ScreenStatusEnum.App &&
      s.status !== apiEnums.ScreenStatusEnum.Half
    );
  });

export const useScreening = <
  SubmitValues extends ApiModel.ScreenContentObject,
>() => {
  const {
    currentStep,
    updateCurrentStep,
    steps,
    updateSteps,
    started,
    updateStarted,
    completed,
    updateCompleted,
  } = useContext(ScreeningContext);
  const apiClient = useApiClient();

  const screensList = useQuery(
    queryKeys.screensList,
    () => apiClient.api.ScreensApi.screensList(),
    { enabled: false },
  );

  const screenSubmit = useMutation(
    (request: ApiModel.ScreensApiScreensSubmitCreateRequest) =>
      apiClient.api.ScreensApi.screensSubmitCreate(request),
  );

  const start = useCallback(() => {
    updateStarted!(true);
  }, [updateStarted]);

  const refresh = useCallback(async () => {
    const { data: steps } = await screensList.refetch();

    if (!steps?.data || !steps?.data.length) {
      return;
    }

    updateSteps!(steps.data);

    const { data: userData } = await apiClient.api.UsersApi.usersMeRetrieve();

    const index = getNextStepIndex(steps.data, userData?.role);
    if (index !== -1) {
      updateCurrentStep!(
        steps.data.length > 0
          ? {
              ...steps.data[index],
              index,
            }
          : undefined,
      );
    } else {
      updateCompleted!(true);
    }
  }, [screensList, updateSteps, updateCurrentStep, updateCompleted, apiClient]);

  const getNextStep = useCallback(() => {
    if (!currentStep) return;

    const nextStep = steps[currentStep.index + 1];

    if (!nextStep) return;

    return {
      ...nextStep,
      index: currentStep.index + 1,
    };
  }, [steps, currentStep]);

  const submit = useCallback<FormSubmitHandler<SubmitValues>>(
    async (data) => {
      if (!currentStep?.id) {
        throw new Error('Screening step id is undefined');
      }

      try {
        const submitResponse = await screenSubmit.mutateAsync({
          id: currentStep.id,
          screenContentObject: data,
        });

        await refresh();

        return submitResponse.data;
      } catch {
        return undefined;
      }
    },
    [refresh, currentStep, screenSubmit],
  );

  return {
    steps: useMemo(
      () =>
        steps.map(({ title, status }, index) => ({
          title,
          status,
          index,
        })),
      [steps],
    ),
    currentStep,
    start,
    started,
    completed,
    refresh,
    submit,
    updateCurrentStep,
    getNextStep,
  };
};
